import { useEffect, useMemo, useState } from "react";
import {
  Candidate,
  CandidateStatus,
  RoleStatus,
  RoleStatusCandidateCount,
  Questions,
} from "../../../Models";
import { useParams } from "react-router-dom";
import useApiToken from "../../../hooks/useApiToken";
import { getNextStatus, RoleStatusType } from "../../../Helpers";
import { Spinner } from "../../Shared/Spinner";
import ConfirmModal from "../../Shared/ConfirmModal";
import BulkActionModalDescription from "./BulkActionModalDescription";
import RoleTableHeader from "./RoleCandidatesHeader";
import RoleCandidatesList from "./RoleCandidatesList";
import { useNotification } from "../../../context/NotificationContext";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import AddCandidateAudioQuestionModal from "../../Shared/Modals/AddCandidateAudioQuestion";
import { paginationData } from "../../../api/config";
import { GetAllQuestionForRole, GetCandidateForRole, GetRole, GetStatusForRole, refreshRoleCandidateCache } from "../../../api/roles.api";
import { CreateMultipleCandidateStatus, getCandidateVideoAnswers, SendCustomEmail, GenerateCandidateCvFlagResult, SubmitFlagCVBatch, GetFlagCVBatchStatus, GetFlagCVPromptPreview, ResendSlotConfirmation, FailedEmailRecipient } from "../../../api/candidates.api";
import FlagCVConfirmModal from "../../Shared/Modals/FlagCVConfirmModal";
import { CandidateRoleAnswersBulkResponse, GetCandidateRoleAnswersBulk } from "../../../api/assessmentquestion.api";
import { GetRoleApplicationScoring } from "../../../api/roleApplicationScoring.api";
import CustomEmailModal from "../../Shared/Modals/CustomEmailModal";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ImportEmailListModal, {
  activeStageIdsFromFilterQuery,
} from "./ImportEmailListModal";
import { CandidateEmailImportResult } from "../../../api/candidates.api";

export interface CandidateAction extends Candidate {
  actions?: CandidateStatus[];
}

/**
 * An uploaded email list currently driving the grid. While this is set the grid shows the matched
 * candidates instead of the paginated role list, so the normal fetches are suspended.
 */
type EmailImportState = {
  result: CandidateEmailImportResult;
  /** Rows handed to the grid at import time, so the banner does not drift as candidates advance out. */
  shownCount: number;
  /** Matched rows deliberately left out — currently the other-stage matches the recruiter declined. */
  skippedMatches: number;
};

export type tab = {
  name: string;
  id: string;
  order: number;
  count: number;
};

export default function RoleCandidates({
  filterQuery,
  searchQuery,
  status,
  statusFilterIsAll,
  rankTypeId,
  handleStatusUpdate,
  selectedColumns,
  importEmailListOpen,
  handleImportEmailListOpen,
}: {
  filterQuery: string;
  searchQuery: string;
  status: RoleStatusCandidateCount;
  statusFilterIsAll: boolean;
  rankTypeId: string;
  handleStatusUpdate: (status: RoleStatusCandidateCount) => void;
  selectedColumns: string[];
  /** Owned by Role so the right sidebar can open it; the modal itself stays with the grid. */
  importEmailListOpen: boolean;
  handleImportEmailListOpen: (open: boolean) => void;
}) {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [paginationData, setPaginationData] = useState<paginationData>();
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate[]>([]);
  const [candidatesWithAction, setCandidatesWithAction] = useState<
    CandidateAction[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortQuery, setSortQuery] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [isShipping, setIsShipping] = useState(false);
  const [actionButtonsDisabled, setActionButtonsDisabled] = useState([
    false,
    false,
    false,
  ]);
  const [bulkActionIsDisqualify, setBulkActionIsDisqualify] = useState(false);
  const [bulkActionModal, setBulkActionModal] = useState(false);
  const [isBulkConfirmDisabled, setIsBulkConfirmDisabled] = useState(false);
  const { success, error } = useNotification();
  const [isAudioModalOpen, setIsAudioModalOpen] = useState<boolean>(false);
  const [selectedPanels, setSelectedPanels] = useState<string[]>([]);
  const [customEmailModalOpen, setCustomEmailModalOpen] = useState(false);
  const [emailImport, setEmailImport] = useState<EmailImportState | null>(null);
  const [flagCVModalOpen, setFlagCVModalOpen] = useState(false);
  const [flagCVPrompt, setFlagCVPrompt] = useState<string | null>(null);
  const [flagCVPromptLoading, setFlagCVPromptLoading] = useState(false);

  const [colomns, setcolomns] = useState<number>(0);
  const [agebased, setagebased] = useState<boolean>(true);
  const [expbased, setexpbased] = useState<boolean>(false);
  const [getAllCandidates, setGetAllCandidates] = useState<boolean>(false);
  const [skillcol, setskillcol] = useState<number>(0);
  const [takeAllCandidates, setTakeAllCandidates] = useState<boolean>(false);
  const [roleListQuestions, setRoleListQuestions] = useState<Questions[]>([]);
  // Candidates who were advanced/booked successfully but whose assessment email failed.
  const [failedEmails, setFailedEmails] = useState<FailedEmailRecipient[]>([]);
  const [isResendingEmails, setIsResendingEmails] = useState<boolean>(false);
  const [candidateRoleAnswersBulk, setCandidateRoleAnswersBulk] =
    useState<CandidateRoleAnswersBulkResponse>({});
  // True when the role has answer weights set, i.e. its candidates carry a Role Q Grading.
  // Roles without scoring don't get an all-empty column.
  const [showRoleQGrading, setShowRoleQGrading] = useState(false);
  useEffect(() => {
    if (agebased) {
      setexpbased(false);
    } else {
      setexpbased(true);
    }
  }, [agebased]);

  const token = useApiToken();
  const { roleId } = useParams();
  useEffect(() => {
    setCandidates([]);
    setCurrentPage(1);
  }, [filterQuery, sortQuery, searchQuery]);

  useEffect(() => {
    if (!roleId || !token) return;
    GetRole(roleId, token).then((rl) => {

      if (rl.department === "Shipping") setIsShipping(true);
      else setIsShipping(false);
    });
    // An imported email list owns the grid until it is cleared. Fetching here would overwrite the
    // matched set with the unfiltered first page the moment the import lands.
    if (emailImport) return;
    setLoading(true);
    GetCandidateForRole(
      roleId,
      token,
      filterQuery,
      1,
      searchQuery,
      sortQuery,
      getAllCandidates
    ).then((cd) => {
      setCandidates(cd.candidates);
      setPaginationData(cd.paginationMetadata);
      setLoading(false);
      if (getAllCandidates) {
        setTakeAllCandidates(true);
      }
    }).catch((e) => {
      setLoading(false);
    });

  }, [roleId, token, filterQuery, searchQuery, sortQuery, rankTypeId, getAllCandidates, emailImport]);

  useEffect(() => {
    if (!roleId || !token) return;
    GetAllQuestionForRole(roleId, token)
      .then((questions) => {
        const listQuestions = (questions ?? []).filter((q: Questions) => Boolean((q as any).showInCandidateList));
        setRoleListQuestions(listQuestions);
      })
      .catch(() => {
        setRoleListQuestions([]);
      });

    GetRoleApplicationScoring(roleId, token)
      .then((scoring) =>
        setShowRoleQGrading(scoring.weights.length > 0 || (scoring.textWeights?.length ?? 0) > 0)
      )
      .catch(() => setShowRoleQGrading(false));
  }, [roleId, token]);

  useEffect(() => {
    if (!roleId || !token) return;
    if (roleListQuestions.length === 0 || candidates.length === 0) {
      setCandidateRoleAnswersBulk({});
      return;
    }
    const candidateIds = candidates.map((c) => c.id);
    const questionIds = roleListQuestions.map((q) => q.id);
    GetCandidateRoleAnswersBulk(roleId, candidateIds, questionIds, token)
      .then((res) => setCandidateRoleAnswersBulk(res ?? {}))
      .catch(() => setCandidateRoleAnswersBulk({}));
  }, [roleId, token, candidates, roleListQuestions]);

  useEffect(() => {
    if (!roleId || !token) return;
    if (currentPage === 1) return;
    if (emailImport) return;
    GetCandidateForRole(
      roleId,
      token,
      filterQuery,
      currentPage,
      searchQuery,
      sortQuery,
      getAllCandidates
    ).then((cd) => {
      if (getAllCandidates) {
        setTakeAllCandidates(true);
      }
      setCandidates((prev) => [...prev, ...cd.candidates]);
      setPaginationData(cd.paginationMetadata);
    });
  }, [
    token,
    roleId,
    currentPage,
    filterQuery,
    searchQuery,
    sortQuery,
    rankTypeId,
    getAllCandidates,
    emailImport
  ]);

  // The import deliberately ignores the sidebar filter and search, so once the recruiter changes
  // either, keeping the imported rows on screen would show a grid that obeys neither. Drop back to
  // the normal list instead.
  useEffect(() => {
    setEmailImport(null);
  }, [roleId, filterQuery, searchQuery, sortQuery, rankTypeId]);

  useEffect(() => {
    setSelectedCandidate([]);
    setGetAllCandidates(false);
    setTakeAllCandidates(false);
    setFilterChanged(true);
  }, [roleId,
    currentPage,
    filterQuery,
    searchQuery,
    sortQuery,
    rankTypeId])
  useEffect(() => {
    const actionCandidates: CandidateAction[] = selectedCandidate
      .map((sc) => {
        const nextStatus = getNextStatus(
          status.roleStatuses,
          sc.candidateStatuses
        );
        const actions =
          nextStatus &&
          nextStatus.map((ns) => {
            const roleStatus = status.roleStatuses.find(
              (rs) => rs.id === ns
            ) as RoleStatus;
            return {
              roleId: roleStatus.roleId,
              candidateId: sc.id,
              roleStatusId: roleStatus.id,
              date: new Date().toISOString(),
              email: true,
              slots: false,
            };
          });
        return {
          ...sc,
          actions,
        };
      })
      .filter((c) => c.actions !== undefined);

    const notHaveDisqualify = !status.roleStatuses.some(
      (rs) => rs.name === RoleStatusType.rejected
    );

    if (actionCandidates.length === 0)
      setActionButtonsDisabled([true, true, false]);
    else if (notHaveDisqualify) setActionButtonsDisabled([false, true, false]);
    else setActionButtonsDisabled([false, false, false]);

    setCandidatesWithAction(actionCandidates);
  }, [selectedCandidate, status.roleStatuses]);

  useEffect(() => {
    const handleScroll = () => {
      // An imported list is already complete - there are no further pages to pull in.
      if (emailImport) return;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      if (scrollTop + windowHeight + 1 >= documentHeight)
        setCurrentPage((prev) =>
          !paginationData
            ? prev
            : prev >= paginationData.totalPages
              ? prev
              : prev + 1
        );
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [paginationData, candidates, emailImport]);

  // useEffect(() => {

  //     const disqualifyStatus=status.roleStatuses.find(rs=>rs.name===RoleStatusType.rejected)
  //     if(!disqualifyStatus)

  // }, [candidatesWithAction]);

  const handleUpdateCandidates = (
    candidatesUpdate: CandidateStatus[],
    gradeStatusUpdate = false,
    manualEvaluation?: { roleStatusId: string; rating: string; score?: number }
  ) => {
    let updatedCandidates: Candidate[];
    if (gradeStatusUpdate) {
      updatedCandidates = candidates.map((c) => {
        if (c.id === candidatesUpdate[0].candidateId) {
          const statusUpdate = c.candidateStatuses.map((cs) => {
            if (
              cs.roleId === candidatesUpdate[0].roleId &&
              cs.roleStatusId === candidatesUpdate[0].roleStatusId
            )
              return candidatesUpdate[0];
            else return cs;
          });

          let updatedManualEvals = c.manualEvaluations || [];
          if (manualEvaluation) {
            const existingIndex = updatedManualEvals.findIndex(
              (e) => e.roleStatusId === manualEvaluation.roleStatusId
            );

            if (manualEvaluation.rating === "") {
              // Remove evaluation if rating is empty (toggling off)
              if (existingIndex >= 0) {
                updatedManualEvals = updatedManualEvals.filter(
                  (e) => e.roleStatusId !== manualEvaluation.roleStatusId
                );
              }
            } else {
              // Update or add evaluation
              if (existingIndex >= 0) {
                updatedManualEvals = [...updatedManualEvals];
                updatedManualEvals[existingIndex] = {
                  ...updatedManualEvals[existingIndex],
                  rating: manualEvaluation.rating as "Excellent" | "Pass" | "Fail",
                  score: manualEvaluation.score,
                };
              } else {
                updatedManualEvals = [
                  ...updatedManualEvals,
                  {
                    id: "",
                    candidateId: c.id,
                    roleId: candidatesUpdate[0].roleId,
                    roleStatusId: manualEvaluation.roleStatusId,
                    rating: manualEvaluation.rating as "Excellent" | "Pass" | "Fail",
                    score: manualEvaluation.score,
                    evaluatedAt: new Date(),
                  },
                ];
              }
            }
          }

          return {
            ...c,
            candidateStatuses: statusUpdate,
            manualEvaluations: updatedManualEvals,
          };
        } else return c;
      });
    } else {
      if (statusFilterIsAll) {
        updatedCandidates = candidates.map((c) => {
          const statusUpdate = candidatesUpdate.find(
            (cu) => cu.candidateId === c.id
          );
          if (statusUpdate)
            return {
              ...c,
              latestStatusDate: statusUpdate.date,
              latestStatusId: statusUpdate.roleStatusId,
              candidateStatuses: [...c.candidateStatuses, statusUpdate],
            };
          else return c;
        });
      } else {
        updatedCandidates = candidates.filter((candidate) => {
          return !candidatesUpdate.some(
            (update) => update.candidateId === candidate.id
          );
        });
      }
    }
    setCandidates(updatedCandidates);
    setSelectedCandidate([]);

    if (!roleId || !token) return;
    GetStatusForRole(roleId, token).then((rs) => {
      handleStatusUpdate(rs);
    });
  };

  // Puts the grid into imported-list mode. The matched candidates become the visible rows and the
  // eligible ones are pre-selected, so Advance / Disqualify / any other bulk action then runs
  // through exactly the same path as a manual multi-select.
  const handleEmailImportApply = (
    result: CandidateEmailImportResult,
    importedCandidates: Candidate[],
    preselected: Candidate[]
  ) => {
    setEmailImport({
      result,
      shownCount: importedCandidates.length,
      skippedMatches: result.candidates.length - importedCandidates.length,
    });
    setCandidates(importedCandidates);
    setSelectedCandidate(preselected);
    setPaginationData(undefined);
    setGetAllCandidates(false);
    setTakeAllCandidates(false);
    handleImportEmailListOpen(false);
    setLoading(false);
  };

  const handleClearEmailImport = () => {
    setEmailImport(null);
    setSelectedCandidate([]);
    // Back to page 1 of the normal list; the suspended fetch re-runs once emailImport clears.
    setCurrentPage(1);
  };

  const handleBulkUpdate = async () => {
    if (!token) return;
    if (isBulkConfirmDisabled) {
      return;
    }
    try {
      const css: CandidateStatus[] = [];
      const actionIndex = bulkActionIsDisqualify ? 1 : 0;
      
      candidatesWithAction.forEach((sc) => {
        const baseAction = sc.actions?.[actionIndex];
        if (!baseAction) return;
        
        if (selectedPanels.length > 0 && baseAction.slots) {
          // Send a single CandidateStatus with comma-separated panels so
          // the candidate can see slots for all selected panels from the link.
          const panelValue = selectedPanels.join(",");
          css.push({
            ...baseAction,
            panel: panelValue,
          });
        } else {
          // No panels selected or slots not enabled - send single status
          css.push(baseAction);
        }
      });
      
      const { statuses: candidateStatusUpdate, emailSummary } =
        await CreateMultipleCandidateStatus(token, css);
      if (roleId && token) refreshRoleCandidateCache(roleId, token);
      handleUpdateCandidates(candidateStatusUpdate);
      setBulkActionModal(false);
      // Candidates can be advanced and booked successfully yet still fail to receive their
      // assessment email, so report delivery separately from the status change.
      if (emailSummary && emailSummary.failed > 0) {
        setFailedEmails(emailSummary.failedCandidates);
        error(
          "Emails partially sent",
          `${emailSummary.sent} sent, ${emailSummary.failed} failed. Review the failed list to resend.`
        );
      } else if (emailSummary && emailSummary.sent > 0) {
        success(
          "Statuses updated",
          `Candidate statuses changed and ${emailSummary.sent} assessment email${
            emailSummary.sent === 1 ? "" : "s"
          } sent successfully.`
        );
      } else {
        success("Statuses updated", "Candidate statuses changed successfully.");
      }
      setGetAllCandidates(false);
      setSelectedPanels([]);
    } catch (er) {
      setBulkActionModal(false);
      console.error("Error advancing candidates:", er);
      error("Error", er instanceof Error ? er.message : "Failed to advance candidates.");
    }
  };

  // Re-sends only the assessment emails that failed. Does not re-advance or re-book
  // anyone — those records were already committed successfully.
  const handleResendFailedEmails = async () => {
    if (!token || failedEmails.length === 0 || isResendingEmails) return;
    setIsResendingEmails(true);
    try {
      // Failures are grouped by slot because each slot's email content differs.
      const bySlot = failedEmails.reduce<Record<string, string[]>>((acc, f) => {
        (acc[f.slotId] ??= []).push(f.candidateId);
        return acc;
      }, {});

      let totalSent = 0;
      const stillFailing: FailedEmailRecipient[] = [];
      for (const [slotId, candidateIds] of Object.entries(bySlot)) {
        const result = await ResendSlotConfirmation(token, candidateIds, slotId);
        totalSent += result.sent;
        stillFailing.push(...result.failedCandidates);
      }

      setFailedEmails(stillFailing);
      if (stillFailing.length === 0) {
        success("Emails sent", `All ${totalSent} remaining email${totalSent === 1 ? "" : "s"} sent successfully.`);
      } else {
        error(
          "Still failing",
          `${totalSent} sent, ${stillFailing.length} still failing. Check the addresses and try again.`
        );
      }
    } catch (er) {
      console.error("Error resending assessment emails:", er);
      error("Error", er instanceof Error ? er.message : "Failed to resend assessment emails.");
    } finally {
      setIsResendingEmails(false);
    }
  };

  const handleMediaAnswerDownload = async () => {
    const candidateIds = selectedCandidate.map((c) => c.id);
    const res = await getCandidateVideoAnswers(candidateIds, token || "");
    if (res.length === 1) {
      const candidateEmail = res[0].email;

      const zip = new JSZip();
      const response = res[0].videoAnswers;
      response.forEach((response: any, index: number) => {
        const videoData = response.answerVideo;
        const responseFolderName = `Video response ${index + 1}`;
        const questionText = response.questionText;
        const responseFolder = zip.folder(
          `${candidateEmail}/${responseFolderName}`
        );

        responseFolder?.file(`${responseFolderName}.mp4`, videoData, {
          base64: true,
        });
        responseFolder?.file(`${responseFolderName}.txt`, questionText);
      });

      zip.generateAsync({ type: "blob" }).then((blob) => {
        saveAs(blob, `${candidateEmail}.zip`);
      });
    } else {
      const zip = new JSZip();
      const parentFolderName = "Candidates Video Responses";

      const parentFolder = zip.folder(parentFolderName);
      if (parentFolder !== null) {
        res.forEach((candidate) => {
          const candidateEmail = candidate.email;
          const candidateAnswers = candidate.videoAnswers;

          const candidateFolder = parentFolder.folder(candidateEmail);
          if (candidateFolder !== null) {
            candidateAnswers.forEach((response: any, index: number) => {
              const videoData = response.answerVideo;
              const responseFolderName = `Video response ${index + 1}`;
              const questionText = response.questionText;
              const responseFolder = candidateFolder.folder(responseFolderName);

              if (responseFolder !== null) {
                responseFolder.file(`${responseFolderName}.mp4`, videoData, {
                  base64: true,
                });
                responseFolder.file(`${responseFolderName}.txt`, questionText);
              }
            });
          }
        });

        zip.generateAsync({ type: "blob" }).then((blob) => {
          saveAs(blob, `${parentFolderName}.zip`);
        });
      }
    }
  };

  const handleAddAudioQuestion = () => {
    setIsAudioModalOpen(true);
  };

  const confirmAdd = async () => {
    setIsAudioModalOpen(false);
  };

  const cancelAdd = () => {
    setIsAudioModalOpen(false);
  };

  const isAudioModalClosed = () => {
    setIsAudioModalOpen(false);
  };

  const uniqueSkills: string[] = candidates
    .flatMap(candidate => candidate.avgSkillScores)
    .reduce<string[]>((acc, skill) => {
      if (!acc.includes(skill.skillName)) {
        acc.push(skill.skillName);
      }
      return acc;
    }, []);

  const handleSendCustomEmail = async (subject: string, body: string) => {
    if (!token) return;
    try {
      const candidateIds = selectedCandidate.map((c) => c.id);
      await SendCustomEmail(candidateIds, subject, body, token);
      success("Success", "Custom emails sent successfully!");
    } catch (err) {
      console.error(err);
      error("Error", "Failed to send custom emails.");
    }
  };

  const STORAGE_KEY = roleId ? `cvFlagBatch_${roleId}` : null;
  const POLL_INTERVAL_MS = 8000;
  const MAX_POLLS = 90; // ~12 minutes max

  /**
   * Starts polling an existing OpenAI batch until it completes, fails, or times out.
   * Clears the localStorage entry when done.
   */
  const startPolling = (batchId: string, currentToken: string) => {
    let polls = 0;
    setActionButtonsDisabled([true, true, true]);

    const pollInterval = setInterval(async () => {
      polls++;
      try {
        const statusResponse = await GetFlagCVBatchStatus(batchId, currentToken);

        if (statusResponse.status === "completed") {
          clearInterval(pollInterval);
          if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
          const count = statusResponse.results?.filter((r) => !r.error).length ?? 0;
          success("Success", `Flagged CVs for ${count} candidate(s)!`);
          setSelectedCandidate([]);
          setActionButtonsDisabled([false, false, false]);
        } else if (
          statusResponse.status === "failed" ||
          statusResponse.status === "expired" ||
          statusResponse.status === "cancelled"
        ) {
          clearInterval(pollInterval);
          if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
          error("Error", `CV flag batch ${statusResponse.status}.`);
          setActionButtonsDisabled([false, false, false]);
        } else if (polls >= MAX_POLLS) {
          clearInterval(pollInterval);
          if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
          error("Timeout", "CV flagging is taking longer than expected. Results will be saved when ready.");
          setActionButtonsDisabled([false, false, false]);
        }
      } catch (pollErr) {
        console.error("Polling error:", pollErr);
        clearInterval(pollInterval);
        if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
        error("Error", "Lost contact while polling for CV flag results.");
        setActionButtonsDisabled([false, false, false]);
      }
    }, POLL_INTERVAL_MS);

    return pollInterval;
  };

  // Resume polling after a page refresh if there was an active batch for this role
  useEffect(() => {
    if (!token || !STORAGE_KEY) return;
    const savedBatchId = localStorage.getItem(STORAGE_KEY);
    if (!savedBatchId) return;
    success("Resuming", "Reconnecting to in-progress CV flagging batch…");
    startPolling(savedBatchId, token);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, roleId]);

  const openFlagCVModal = async () => {
    if (!token || !roleId || !STORAGE_KEY) return;
    setFlagCVModalOpen(true);
    // If a batch is already in progress, we just show the modal in "in progress" state
    if (localStorage.getItem(STORAGE_KEY)) return;
    setFlagCVPrompt(null);
    setFlagCVPromptLoading(true);
    try {
      const { prompt } = await GetFlagCVPromptPreview(roleId, token);
      setFlagCVPrompt(prompt);
    } catch (err) {
      console.error(err);
      setFlagCVPrompt(null);
    } finally {
      setFlagCVPromptLoading(false);
    }
  };

  const handleFlagCV = async () => {
    if (!token || !roleId || !STORAGE_KEY) return;
    setFlagCVModalOpen(false);
    try {
      setActionButtonsDisabled([true, true, true]);
      const candidateIds = selectedCandidate.map((c) => c.id);

      // Submit all candidates as a single OpenAI batch job
      const { batchId } = await SubmitFlagCVBatch(roleId, candidateIds, token);

      // Persist so polling can resume if the user refreshes the page
      localStorage.setItem(STORAGE_KEY, batchId);

      success(
        "Batch submitted",
        `CV flagging started for ${candidateIds.length} candidate(s). You can safely refresh — it will resume automatically.`
      );

      startPolling(batchId, token);
    } catch (err) {
      console.error(err);
      error("Error", "Failed to submit CV flag batch.");
      setActionButtonsDisabled([false, false, false]);
    }
  };

  // The sidebar encodes the selected status tab(s) into the filter query, which is the only signal
  // this component gets for "the stage currently on screen".
  const activeStageIds = useMemo(
    () => activeStageIdsFromFilterQuery(filterQuery),
    [filterQuery]
  );

  // Counted in addresses, not rows: one address can match several candidate records on a role, so
  // subtracting rows from addresses would under- (or negatively) report what the file left behind.
  const importedUnmatched = emailImport
    ? emailImport.result.emailsNotOnRoleCount + emailImport.result.emailsNotFoundCount
    : 0;

  return (
    <div className="min-h-full relative" id="candidates">
      {emailImport && (
        <div className="flex flex-wrap items-center gap-3 py-2">
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm dark:border-entntblue dark:bg-entntblue/20">
            <span className="truncate text-blue-900 dark:text-gray-200">
              Filtered to{" "}
              <span className="font-semibold">
                {emailImport.shownCount} candidate{emailImport.shownCount === 1 ? "" : "s"}
              </span>{" "}
              from {emailImport.result.fileName ?? "a pasted list"}
              {importedUnmatched > 0 && (
                <span className="text-blue-700 dark:text-gray-300">
                  {" "}
                  · {importedUnmatched} of {emailImport.result.emailsParsed} address
                  {emailImport.result.emailsParsed === 1 ? "" : "es"} matched no candidate here
                </span>
              )}
              {emailImport.skippedMatches > 0 && (
                <span className="text-blue-700 dark:text-gray-300">
                  {" "}
                  · {emailImport.skippedMatches} matched but skipped
                </span>
              )}
            </span>
            <button
              type="button"
              className="ml-auto inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold text-blue-800 hover:bg-blue-100 dark:text-gray-200 dark:hover:bg-entntblue/40"
              onClick={handleClearEmailImport}
            >
              <XMarkIcon className="h-4 w-4" />
              Clear
            </button>
          </div>
        </div>
      )}

      <table className="min-w-full table-fixed divide-y">
        <RoleTableHeader
          candidates={candidates}
          selectedCandidate={selectedCandidate}
          actionButtonsDisabled={actionButtonsDisabled}
          handleSelectedCandidates={(candidates) => {
            setSelectedCandidate(candidates);
          }}
          handleSort={(query: string) => {
            setSortQuery(query);
          }}
          handleGetAllCandidates={(query: boolean) => {
            setGetAllCandidates(query);
          }}
          getAllCandidates={getAllCandidates}
          handleBulkActionIsDisqualify={(open) => {
            setBulkActionIsDisqualify(open);
          }}
          handleBulkActionModal={(open) => {
            setBulkActionModal(open);
          }}
          handleCustomEmailModal={(open) => {
            setCustomEmailModalOpen(open);
          }}
          handleFlagCV={openFlagCVModal}
          handleMediaAnswerDownload={handleMediaAnswerDownload}
          handleAddAudioQuestion={handleAddAudioQuestion}
          selectedColoumns={selectedColumns}
          setcolomns={setcolomns}
          colomns={colomns}
          setagebased={setagebased}
          agebased={agebased}
          uniqueSkills={uniqueSkills}
          skillcol={skillcol}
          setskillcol={setskillcol}
          roleListQuestions={roleListQuestions}
          showRoleQGrading={showRoleQGrading}
          status={status}
          isShipping={isShipping}
          takeAllCandidates={takeAllCandidates}
          setTakeAllCandidates={setTakeAllCandidates}
          filterChanged={filterChanged}
          setFilterChanged={setFilterChanged}
        />

        {loading ? (
          <tbody>
            <tr className="h-80">
              <td colSpan={11}>
                <Spinner height="h-10" width="w-10" />
              </td>
            </tr>
          </tbody>
        ) : candidates.length === 0 ? (
          <tbody>
            <tr className="h-80">
              <td
                colSpan={11}
                className="text-center mt-20 text-base leading-7 text-gray-600 dark:text-gray-400"
              >
                No candidates found{" "}
              </td>
            </tr>
          </tbody>
        ) : ( 
          <RoleCandidatesList
            isShipping={isShipping}
            selectedCandidate={selectedCandidate}
            handleSelectedCandidate={(candidate) => {
              setSelectedCandidate(candidate);
            }}
            handleUpdateCandidates={handleUpdateCandidates}
            candidates={candidates}
            status={status}
            colomns={colomns}
            agebased={agebased}
            expbased={expbased}
            uniqueSkills={uniqueSkills}
            skillcol={skillcol}
            setskillcol={setskillcol}
            roleListQuestions={roleListQuestions}
            candidateRoleAnswersBulk={candidateRoleAnswersBulk}
            showRoleQGrading={showRoleQGrading}
          />
        )}
      </table>

      {/* Assessment emails that failed to send during the last bulk advance.
          The candidates ARE advanced and booked — only their mail did not arrive. */}
      {failedEmails.length > 0 && (
        <div className="mx-4 my-4 rounded-md border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/30">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                {failedEmails.length} assessment email
                {failedEmails.length === 1 ? "" : "s"} could not be sent
              </h3>
              <p className="mt-1 text-xs text-red-700 dark:text-red-400">
                These candidates were advanced and booked successfully — only the email
                failed. They will not have their assessment link until it is resent.
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={handleResendFailedEmails}
                disabled={isResendingEmails}
                className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isResendingEmails ? "Resending..." : "Retry failed"}
              </button>
              <button
                type="button"
                onClick={() => setFailedEmails([])}
                disabled={isResendingEmails}
                className="inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-60"
              >
                Dismiss
              </button>
            </div>
          </div>
          <ul className="mt-3 max-h-40 overflow-y-auto text-xs text-red-800 dark:text-red-300">
            {failedEmails.map((f) => (
              <li key={f.candidateId} className="py-0.5">
                {f.name || "Unknown"} — {f.email}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmModal
        open={bulkActionModal}
        setOpen={setBulkActionModal}
        title={
          bulkActionIsDisqualify
            ? `Disqualify ${selectedCandidate.length} candidate${selectedCandidate.length !== 1 ? "s" : ""
            }`
            : `Advance ${selectedCandidate.length} candidate${selectedCandidate.length !== 1 ? "s" : ""
            }`
        }
        description={
          <BulkActionModalDescription
            handleCandidateWithAction={(c) => {
              setCandidatesWithAction(c);
            }}
            candidatesWithAction={candidatesWithAction}
            roleStatuses={status.roleStatuses}
            setSelectedCandidate={setSelectedCandidate}
            bulkActionIsDisqualify={bulkActionIsDisqualify}
            handleBulkConfirmButtonDisabled={(query) => setIsBulkConfirmDisabled(query)}
            onPanelsSelected={(panels) => setSelectedPanels(panels)}
          />
        }
        onConfirm={handleBulkUpdate}
        type={bulkActionIsDisqualify ? "danger" : "none"}
        maxWidth="sm:max-w-xl"
        scrollBody
      />

      {isAudioModalOpen && (
        <AddCandidateAudioQuestionModal
          title=""
          content=""
          onAdd={confirmAdd}
          onCancel={cancelAdd}
          isModalClosed={isAudioModalClosed}
          size="large"
          selectedCandidateIds={selectedCandidate.map((e) => e.id)}
        />
      )}

      {customEmailModalOpen && (
        <CustomEmailModal
          open={customEmailModalOpen}
          setOpen={setCustomEmailModalOpen}
          onSend={handleSendCustomEmail}
          candidatesCount={selectedCandidate.length}
        />
      )}

      <FlagCVConfirmModal
        open={flagCVModalOpen}
        onClose={() => setFlagCVModalOpen(false)}
        onConfirm={handleFlagCV}
        candidateCount={selectedCandidate.length}
        prompt={flagCVPrompt}
        promptLoading={flagCVPromptLoading}
        isInProgress={Boolean(STORAGE_KEY && localStorage.getItem(STORAGE_KEY))}
      />

      {roleId && (
        <ImportEmailListModal
          open={importEmailListOpen}
          onClose={() => handleImportEmailListOpen(false)}
          roleId={roleId}
          roleStatuses={status.roleStatuses}
          activeStageIds={activeStageIds}
          statusFilterIsAll={statusFilterIsAll}
          sortQuery={sortQuery}
          onApply={handleEmailImportApply}
        />
      )}
    </div>
  );
}
